/**
 * SgpFilterPanel — search + filter panel for SgpFlowScreen (classic UI).
 * Extracted verbatim from SgpFlowScreen.jsx (god-file decomposition).
 * Parent guards with `!shouldUseNewResultsUI && fetchedOptions.length > 0`.
 *
 * Completeness of this prop surface is machine-verified by
 * `scripts/check-extraction.js` (every referenced identifier is bound).
 */

import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Pressable, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { styles } from '../screens/sgpFlowStyles';
import HoverTag from './HoverTag';
import GradientButton from './GradientButton';
import PipelineDebugPanel from './PipelineDebugPanel';
import { MAJOR_SPORTSBOOKS, SECONDARY_SPORTSBOOKS, INTERNATIONAL_SPORTSBOOKS, DFS_SPORTSBOOKS } from '../../utils/sportsbookTiers';
import { explainFilterDrop, formatFilterDropSubtitle } from '../utils/filterStageExplain';
import { describeResultCounts } from '../utils/resultCounts';
import { countCards } from '../utils/cardGrouping';
import { useHideNoEvRows, effectiveMinBooksStraights } from '../utils/searchDisplay';
import AnimatedNumber from './motion/AnimatedNumber';
import PressableScale from './motion/PressableScale';

export default function SgpFilterPanel({
_isAdminTop, _userPickedSortRef, availableLegCounts, availableMarkets, availablePlayers, availableSportsbooks, canShowDevResultsCopy, clearAllFilters, copyResultsFlash, debugPanelExpanded, fetchedOptions, filterDeepLinksOnly, filterLegCount, filterLineType, filterMarkets, filterPlayers, filterPositiveEvOnly, filterResultType, filterSportsbooks, filteredAndSortedOptions, handleCopyAllResults, handleQueryFeedback, hasPropsResults, hasTeamResults, isLiveQuery, isSgpResults, playerChipHeadshots, queryFeedback, responseMetadata, searchText, searchedSportsbooks, setCurrentPage, setDebugPanelExpanded, setFilterDeepLinksOnly, setFilterLegCount, setFilterLineType, setFilterMarkets, setFilterPlayers, setFilterPositiveEvOnly, setFilterResultType, setFilterSportsbooks, setSearchText, setSortBy, setSortMethod, sortBy, sortMethod, toggleMarketFilter, togglePlayerFilter, toggleSportsbookFilter, userProfile
}) {
  const [sportsbooksExpanded, setSportsbooksExpanded] = useState(false);
  const [playersExpanded, setPlayersExpanded] = useState(false);
  const minBooks = effectiveMinBooksStraights(userProfile?.preferences?.minBooksStraights, useHideNoEvRows());
  const filterDrop = explainFilterDrop(filteredAndSortedOptions?.filterStageStats);
  const filterDropSubtitle = formatFilterDropSubtitle(filterDrop, minBooks);
  const hiddenByFilters = fetchedOptions.length > filteredAndSortedOptions.length;
  // Same unit and wording as the Background Queries card and the book toggle.
  const shownLines = filteredAndSortedOptions.length;
  const shownCards = countCards(filteredAndSortedOptions);
  const countHeadline = describeResultCounts({ lines: shownLines, cards: shownCards, sgp: !!isSgpResults });
  const unreliableOnly = filteredAndSortedOptions?.unreliableOnlyBooks;
  return (
                          <View style={styles.filterPanel}>
                            {/* Header with Stats and Feedback */}
                            <View style={styles.filterHeader}>
                              <View style={{ flex: 1 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                  <Text style={styles.filterTitle} accessibilityLabel={countHeadline}>
                                    {isSgpResults ? (
                                      <><AnimatedNumber value={shownLines} style={styles.filterTitle} /> {shownLines === 1 ? 'SGP' : 'SGPs'}</>
                                    ) : shownCards === shownLines ? (
                                      <><AnimatedNumber value={shownLines} style={styles.filterTitle} /> {shownLines === 1 ? 'line' : 'lines'}</>
                                    ) : (
                                      <>
                                        <AnimatedNumber value={shownLines} style={styles.filterTitle} /> {shownLines === 1 ? 'line' : 'lines'} in{' '}
                                        <AnimatedNumber value={shownCards} style={styles.filterTitle} /> {shownCards === 1 ? 'card' : 'cards'}
                                      </>
                                    )}
                                  </Text>
                                  {isLiveQuery && (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2, borderWidth: 1, borderColor: 'rgba(16,185,129,0.4)' }}>
                                      <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: '#10B981' }} />
                                      <Text style={{ color: '#10B981', fontSize: 10, fontWeight: '700', letterSpacing: 0.5 }}>LIVE</Text>
                                    </View>
                                  )}
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                  <Text style={styles.filterSubtitle}>Searched:</Text>
                                  {searchedSportsbooks.slice(0, 3).map((book, idx) => (
                                    <Text key={idx} style={styles.searchedBookTag}>{book}</Text>
                                  ))}
                                  {searchedSportsbooks.length > 3 && (
                                    <Text style={styles.filterSubtitle}>+{searchedSportsbooks.length - 3} more</Text>
                                  )}
                                </View>
                                {unreliableOnly ? (
                                  <Text style={[styles.filterSubtitle, { marginTop: 6, color: '#f59e0b', maxWidth: 520 }]}>
                                    {`Only prediction markets / offshore books priced these (${unreliableOnly.join(', ')}) — no sportsbook had posted them when the odds were captured. Treat the EV with caution.`}
                                  </Text>
                                ) : null}
                                {filterDropSubtitle ? (
                                  <Text style={[styles.filterSubtitle, { marginTop: 6, color: 'rgba(255,255,255,0.55)', maxWidth: 520 }]}>
                                    {filterDropSubtitle}
                                  </Text>
                                ) : null}
                              </View>
                              
                              {/* AI Feedback */}
                              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>How did AI do?</Text>
                                <PressableScale
                                  onPress={() => handleQueryFeedback('up')}
                                  style={[styles.feedbackButton, queryFeedback === 'up' && styles.feedbackButtonActive]}
                                  hoverStyle={queryFeedback === 'up' ? null : styles.feedbackButtonHovered}
                                  scaleTo={0.9}
                                  accessibilityLabel={'up' === 'up' ? 'Good result' : 'Bad result'}
                                  accessibilityState={{ selected: queryFeedback === 'up' }}
                                >
                                  <Text style={{ fontSize: 16 }}>{queryFeedback === 'up' ? '👍' : '👍🏻'}</Text>
                                </PressableScale>
                                <PressableScale
                                  onPress={() => handleQueryFeedback('down')}
                                  style={[styles.feedbackButton, queryFeedback === 'down' && styles.feedbackButtonActive]}
                                  hoverStyle={queryFeedback === 'down' ? null : styles.feedbackButtonHovered}
                                  scaleTo={0.9}
                                  accessibilityLabel={'down' === 'up' ? 'Good result' : 'Bad result'}
                                  accessibilityState={{ selected: queryFeedback === 'down' }}
                                >
                                  <Text style={{ fontSize: 16 }}>{queryFeedback === 'down' ? '👎' : '👎🏻'}</Text>
                                </PressableScale>
                              </View>
                            </View>

                            {/* Search Bar */}
                            <View style={styles.searchContainer}>
                              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 20, marginRight: 8 }}>🔍</Text>
                              <TextInput
                                style={styles.searchInput}
                                value={searchText}
                                onChangeText={setSearchText}
                                placeholder="Search by team, player, market, sportsbook..."
                                placeholderTextColor="rgba(255,255,255,0.4)"
                              />
                              {searchText ? (
                                <PressableScale onPress={() => setSearchText('')} style={{ padding: 4 }}>
                                  <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }}>✕</Text>
                                </PressableScale>
                              ) : null}
                            </View>

                            {/* Filters Row */}
                            <View style={styles.filtersRow}>

                              {/* Sportsbook Filter */}
                              <View style={styles.filterSection}>
                                <View style={styles.filterSectionHeader}>
                                  <Text style={styles.filterLabel}>Sportsbook</Text>
                                  {filterSportsbooks.length > 0 && (
                                    <PressableScale onPress={() => setFilterSportsbooks([])}>
                                      <Text style={styles.clearButton}>Clear ({filterSportsbooks.length})</Text>
                                    </PressableScale>
                                  )}
                                </View>
                                <View style={styles.filterTagsContainer}>
                                  {(() => {
                                    const myBooks = userProfile?.preferences?.myBooks || [];
                                    const resultsSet = new Set(availableSportsbooks);
                                    // Build tier-ordered list using only books present in results
                                    const tierOrder = [
                                      ...MAJOR_SPORTSBOOKS,
                                      ...SECONDARY_SPORTSBOOKS,
                                      ...INTERNATIONAL_SPORTSBOOKS,
                                      ...DFS_SPORTSBOOKS,
                                    ];
                                    // Books in results sorted by tier priority, with unrecognised books at end
                                    const inTier = tierOrder.filter(b => resultsSet.has(b));
                                    const notInTier = availableSportsbooks.filter(b => !tierOrder.includes(b));
                                    const finalList = [...inTier, ...notInTier];

                                    if (finalList.length === 0) {
                                      return <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>No sportsbooks in results</Text>;
                                    }

                                    const CHIP_PREVIEW = 12;
                                    const visibleBooks = sportsbooksExpanded || finalList.length <= CHIP_PREVIEW
                                      ? finalList
                                      : finalList.slice(0, CHIP_PREVIEW);

                                    return (
                                    <>
                                    {visibleBooks.map(book => {
                                      const bookNorm = book.toLowerCase().replace(/[^a-z0-9]/g, '');
                                      const isMyBook = myBooks.some(mb => mb.toLowerCase().replace(/[^a-z0-9]/g, '') === bookNorm || mb.toLowerCase().includes(bookNorm) || bookNorm.includes(mb.toLowerCase()));
                                      const isActive = filterSportsbooks.includes(book);
                                      return (
                                        <HoverTag
                                          key={book}
                                          onPress={() => toggleSportsbookFilter(book)}
                                          isActive={isActive}
                                          style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            paddingVertical: 7,
                                            paddingHorizontal: 12,
                                            borderRadius: 6,
                                            backgroundColor: isActive ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.06)',
                                            borderWidth: isMyBook ? 2 : 1,
                                            borderColor: isMyBook ? '#8B5CF6' : (isActive ? '#4ade80' : 'rgba(255,255,255,0.15)'),
                                          }}
                                          hoverStyle={isActive ? null : (isMyBook ? { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: '#a78bfa' } : undefined)}
                                        >
                                          {isMyBook && <Text style={{ color: '#8B5CF6', fontSize: 10, marginRight: 4, pointerEvents: 'none' }}>⭐</Text>}
                                          <Text style={{
                                            color: isActive ? '#4ade80' : (isMyBook ? '#c4b5fd' : 'rgba(255,255,255,0.85)'),
                                            fontSize: 12,
                                            fontWeight: '600',
                                            pointerEvents: 'none',
                                          }}>
                                            {book}
                                          </Text>
                                        </HoverTag>
                                      );
                                    })}
                                    {finalList.length > CHIP_PREVIEW && (
                                      <PressableScale
                                        onPress={() => setSportsbooksExpanded((v) => !v)}
                                        style={{ paddingVertical: 7, paddingHorizontal: 12, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)' }}
                                        hoverStyle={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.3)' }}
                                        scaleTo={0.96}
                                      >
                                        <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: '600' }}>
                                          {sportsbooksExpanded ? 'Show fewer sportsbooks' : `Show all ${finalList.length} sportsbooks`}
                                        </Text>
                                      </PressableScale>
                                    )}
                                    </>
                                    );
                                  })()}
                                </View>
                              </View>

                              {/* Result Type Filter — shows when both types are present so user can filter */}
                              {hasTeamResults && hasPropsResults && (
                              <View style={styles.filterSection}>
                                <View style={styles.filterSectionHeader}>
                                  <Text style={styles.filterLabel}>Result Type</Text>
                                  {filterResultType !== 'all' && (
                                    <PressableScale onPress={() => { setFilterResultType('all'); setCurrentPage(1); }}>
                                      <Text style={styles.clearButton}>Clear</Text>
                                    </PressableScale>
                                  )}
                                </View>
                                <View style={styles.filterTagsContainer}>
                                  {['all', 'team', 'props'].map(type => {
                                    const isActive = filterResultType === type;
                                    return (
                                      <HoverTag
                                        key={type}
                                        onPress={() => { setFilterResultType(type); setCurrentPage(1); }}
                                        style={[styles.filterTag, isActive && styles.filterTagActive]}
                                        isActive={isActive}
                                      >
                                        <Text style={[styles.filterTagText, isActive && styles.filterTagTextActive]}>
                                          {type === 'all' ? 'All' : type === 'team' ? 'Team Odds' : 'Player Props'}
                                        </Text>
                                      </HoverTag>
                                    );
                                  })}
                                </View>
                              </View>
                              )}

                              {/* Market Filter — for SGPs, chips are leg markets (Points, Assists…); use Legs for 2 vs 3 */}
                              <View style={styles.filterSection}>
                                <View style={styles.filterSectionHeader}>
                                  <Text style={styles.filterLabel}>{isSgpResults ? 'Markets in parlay' : 'Market Type'}</Text>
                                  {filterMarkets.length > 0 && (
                                    <PressableScale onPress={() => setFilterMarkets([])}>
                                      <Text style={styles.clearButton}>Clear ({filterMarkets.length})</Text>
                                    </PressableScale>
                                  )}
                                </View>
                                <View style={styles.filterTagsContainer}>
                                  {availableMarkets.length > 0 ? availableMarkets.map(market => {
                                    const isActive = filterMarkets.includes(market);
                                    return (
                                      <HoverTag
                                        key={market}
                                        onPress={() => toggleMarketFilter(market)}
                                        style={[styles.filterTag, isActive && styles.filterTagActive]}
                                        isActive={isActive}
                                      >
                                        <Text style={[styles.filterTagText, isActive && styles.filterTagTextActive]}>
                                          {market}
                                        </Text>
                                      </HoverTag>
                                    );
                                  }) : <Text style={styles.noFiltersText}>No markets</Text>}
                                </View>
                              </View>

                              {/* Legs Filter (SGP mode only) — 2-leg vs 3-leg, etc. */}
                              {isSgpResults && availableLegCounts.length > 0 && (
                                <View style={styles.filterSection}>
                                  <View style={styles.filterSectionHeader}>
                                    <Text style={styles.filterLabel}>Parlay size</Text>
                                    {filterLegCount !== null && (
                                      <PressableScale onPress={() => setFilterLegCount(null)}>
                                        <Text style={styles.clearButton}>Clear</Text>
                                      </PressableScale>
                                    )}
                                  </View>
                                  <View style={styles.filterTagsContainer}>
                                    <HoverTag
                                      onPress={() => setFilterLegCount(null)}
                                      style={[styles.filterTag, filterLegCount === null && styles.filterTagActive]}
                                      isActive={filterLegCount === null}
                                    >
                                      <Text style={[styles.filterTagText, filterLegCount === null && styles.filterTagTextActive]}>All</Text>
                                    </HoverTag>
                                    {availableLegCounts.map(lc => {
                                      const isActive = filterLegCount === lc;
                                      return (
                                        <HoverTag
                                          key={lc}
                                          onPress={() => setFilterLegCount(isActive ? null : lc)}
                                          style={[styles.filterTag, isActive && styles.filterTagActive]}
                                          isActive={isActive}
                                        >
                                          <Text style={[styles.filterTagText, isActive && styles.filterTagTextActive]}>{lc}-Leg</Text>
                                        </HoverTag>
                                      );
                                    })}
                                  </View>
                                </View>
                              )}

                              {/* Player Filter — shown for both player props and SGP results */}
                              {availablePlayers.length > 0 && (
                                <View style={styles.filterSection}>
                                  <View style={styles.filterSectionHeader}>
                                    <Text style={styles.filterLabel}>Player</Text>
                                    {filterPlayers.length > 0 && (
                                      <PressableScale onPress={() => setFilterPlayers([])}>
                                        <Text style={styles.clearButton}>Clear ({filterPlayers.length})</Text>
                                      </PressableScale>
                                    )}
                                  </View>
                                  <View style={styles.filterTagsContainer}>
                                    {(() => {
                                      const PLAYER_PREVIEW = 12;
                                      const visiblePlayers = playersExpanded || availablePlayers.length <= PLAYER_PREVIEW
                                        ? availablePlayers
                                        : availablePlayers.slice(0, PLAYER_PREVIEW);
                                      return (
                                        <>
                                    {visiblePlayers.map(({ name: playerName, headshotUrl }) => {
                                      const isActive = filterPlayers.includes(playerName);
                                      const photo = headshotUrl || playerChipHeadshots[playerName] || null;
                                      return (
                                        <HoverTag
                                          key={playerName}
                                          onPress={() => togglePlayerFilter(playerName)}
                                          style={[styles.filterTag, isActive && styles.filterTagActive, { flexDirection: 'row', alignItems: 'center', gap: 6 }]}
                                          isActive={isActive}
                                        >
                                          {photo ? (
                                            <Image
                                              source={{ uri: photo }}
                                              style={{ width: 22, height: 22, borderRadius: 11 }}
                                            />
                                          ) : (
                                            <View style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', alignItems: 'center', justifyContent: 'center' }}>
                                              <Text style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.5)', fontSize: 8, fontWeight: '700' }}>
                                                {playerName.split(' ').map(w => w[0]).slice(0, 2).join('')}
                                              </Text>
                                            </View>
                                          )}
                                          <Text style={[styles.filterTagText, isActive && styles.filterTagTextActive]}>
                                            {playerName}
                                          </Text>
                                        </HoverTag>
                                      );
                                    })}
                                    {availablePlayers.length > PLAYER_PREVIEW && (
                                      <HoverTag
                                        onPress={() => setPlayersExpanded((v) => !v)}
                                        style={styles.filterTag}
                                        isActive={false}
                                      >
                                        <Text style={styles.filterTagText}>
                                          {playersExpanded ? 'Show fewer players' : `Show all ${availablePlayers.length} players`}
                                        </Text>
                                      </HoverTag>
                                    )}
                                        </>
                                      );
                                    })()}
                                  </View>
                                </View>
                              )}

                              {/* Sort Options */}
                              <View style={styles.filterSection}>
                                <Text style={styles.filterLabel}>Additional Sorting</Text>
                                <View style={styles.filterTagsContainer}>
                                  {[
                                    { value: 'ev', label: 'Best EV' },
                                    { value: 'date', label: 'Date' },
                                    { value: 'price_desc', label: 'Highest Odds' },
                                    { value: 'price_asc', label: 'Lowest Odds' },
                                    { value: 'sportsbooks', label: 'Most Sportsbooks' },
                                    { value: 'market', label: 'Market A-Z' }
                                  ].map(sort => {
                                    const isActive = sortBy === sort.value;
                                    return (
                                      <HoverTag
                                        key={sort.value}
                                        onPress={() => {
                                          _userPickedSortRef.current = true;
                                          setSortBy(sort.value);
                                          if (sort.value === 'ev') setSortMethod('ev');
                                          else if (sort.value === 'price_desc' || sort.value === 'price_asc') setSortMethod('odds');
                                        }}
                                        style={[styles.filterTag, isActive && styles.filterTagActive]}
                                        isActive={isActive}
                                      >
                                        <Text style={[styles.filterTagText, isActive && styles.filterTagTextActive]}>
                                          {sort.label}
                                        </Text>
                                      </HoverTag>
                                    );
                                  })}
                                  
                                  {/* Deep Links Only Toggle */}
                                  <PressableScale
                                    onPress={() => setFilterDeepLinksOnly(!filterDeepLinksOnly)}
                                    accessibilityRole="switch"
                                    accessibilityState={{ checked: !!filterDeepLinksOnly }}
                                    style={[
                                      styles.filterTag,
                                      { borderColor: filterDeepLinksOnly ? '#10B981' : 'rgba(16, 185, 129, 0.4)', borderWidth: 1, marginLeft: 8 },
                                      filterDeepLinksOnly && { backgroundColor: 'rgba(16, 185, 129, 0.2)' },
                                    ]}
                                    hoverStyle={filterDeepLinksOnly ? null : { backgroundColor: 'rgba(16, 185, 129, 0.12)', borderColor: 'rgba(16, 185, 129, 0.65)' }}
                                    scaleTo={0.96}
                                  >
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                      <Ionicons name="link-outline" size={11} color={filterDeepLinksOnly ? '#10B981' : 'rgba(16, 185, 129, 0.7)'} />
                                      <Text style={[styles.filterTagText, { color: filterDeepLinksOnly ? '#10B981' : 'rgba(16, 185, 129, 0.7)' }]}>
                                        Direct Links Only
                                      </Text>
                                    </View>
                                  </PressableScale>

                                  {/* Positive-EV only toggle */}
                                  <PressableScale
                                    onPress={() => setFilterPositiveEvOnly(!filterPositiveEvOnly)}
                                    accessibilityRole="switch"
                                    accessibilityState={{ checked: !!filterPositiveEvOnly }}
                                    style={[
                                      styles.filterTag,
                                      { borderColor: filterPositiveEvOnly ? '#4ade80' : 'rgba(74, 222, 128, 0.4)', borderWidth: 1, marginLeft: 8 },
                                      filterPositiveEvOnly && { backgroundColor: 'rgba(74, 222, 128, 0.2)' },
                                    ]}
                                    hoverStyle={filterPositiveEvOnly ? null : { backgroundColor: 'rgba(74, 222, 128, 0.1)', borderColor: 'rgba(74, 222, 128, 0.65)' }}
                                    scaleTo={0.96}
                                  >
                                    <Text style={[styles.filterTagText, { color: filterPositiveEvOnly ? '#4ade80' : 'rgba(74, 222, 128, 0.7)' }]}>
                                      +EV Only
                                    </Text>
                                  </PressableScale>
                                  
                                  {/* Line Type Filter — Main/Alt apply to props AND game lines */}
                                  <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 12, gap: 4 }}>
                                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginRight: 4 }}>Lines:</Text>
                                    {(() => {
                                      const splits = filteredAndSortedOptions?.lineTypeCounts || null;
                                      return [
                                        { value: 'all', label: 'All', icon: null, count: splits?.all },
                                        { value: 'main', label: 'Main', icon: 'flag-outline', count: splits?.main },
                                        { value: 'alt', label: 'Alt', icon: 'git-branch-outline', count: splits?.alt },
                                      ];
                                    })().map(lineType => {
                                      const isActive = filterLineType === lineType.value;
                                      const activeColor = lineType.value === 'main' ? '#2E7D32' : lineType.value === 'alt' ? '#E65100' : '#3B82F6';
                                      const activeBg = lineType.value === 'main' ? 'rgba(46,125,50,0.2)' : lineType.value === 'alt' ? 'rgba(230,81,0,0.2)' : 'rgba(59,130,246,0.2)';
                                      const activeText = lineType.value === 'main' ? '#4CAF50' : lineType.value === 'alt' ? '#FF9800' : '#3B82F6';
                                      const countLabel = Number.isFinite(lineType.count) ? ` (${lineType.count})` : '';
                                      return (
                                        <HoverTag
                                          key={lineType.value}
                                          onPress={() => { setFilterLineType(lineType.value); setCurrentPage(1); }}
                                          isActive={isActive}
                                          style={[
                                            styles.filterTag,
                                            { paddingHorizontal: 8, borderColor: isActive ? activeColor : 'rgba(255,255,255,0.2)', borderWidth: 1 },
                                            isActive && { backgroundColor: activeBg },
                                          ]}
                                        >
                                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                            {lineType.icon && (
                                              <Ionicons name={lineType.icon} size={10} color={isActive ? activeText : 'rgba(255,255,255,0.6)'} />
                                            )}
                                            <Text style={[styles.filterTagText, { color: isActive ? activeText : 'rgba(255,255,255,0.6)', fontSize: 11 }]}>
                                              {lineType.label}{countLabel}
                                            </Text>
                                          </View>
                                        </HoverTag>
                                      );
                                    })}
                                  </View>

                                  {/* v1.16.x — admin/dev-only "Copy all results" button. Copies the whole
                                      currently-ranked card list with pill/sort EV, book counts, _ev_source,
                                      and alt-line ladders so we can diagnose sort / contamination issues
                                      from one paste. Mirrors the per-card COPY button we added in v1.16.3. */}
                                  {canShowDevResultsCopy && (
                                    <PressableScale
                                      onPress={handleCopyAllResults}
                                      accessibilityLabel="Copy all results (debug)"
                                      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                                      style={{
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 4,
                                        marginLeft: 12,
                                        paddingHorizontal: 8,
                                        paddingVertical: 4,
                                        borderRadius: 4,
                                        backgroundColor: copyResultsFlash ? 'rgba(74,222,128,0.18)' : 'rgba(255,255,255,0.06)',
                                        borderWidth: 1,
                                        borderColor: copyResultsFlash ? 'rgba(74,222,128,0.55)' : 'rgba(255,255,255,0.18)',
                                      }}
                                    >
                                      <Ionicons
                                        name={copyResultsFlash ? 'checkmark' : 'copy-outline'}
                                        size={12}
                                        color={copyResultsFlash ? 'rgba(74,222,128,0.95)' : 'rgba(255,255,255,0.7)'}
                                      />
                                      <Text style={{
                                        fontSize: 10,
                                        fontWeight: '600',
                                        color: copyResultsFlash ? 'rgba(74,222,128,0.95)' : 'rgba(255,255,255,0.7)',
                                        letterSpacing: 0.4,
                                      }}>
                                        {copyResultsFlash ? 'COPIED' : 'COPY RESULTS'}
                                      </Text>
                                    </PressableScale>
                                  )}
                                </View>
                              </View>

                            </View>

                            {/* Admin-only pipeline debug panel */}
                            {_isAdminTop && responseMetadata && (
                              <PipelineDebugPanel
                                responseMetadata={responseMetadata}
                                expanded={debugPanelExpanded}
                                onToggle={() => setDebugPanelExpanded(e => !e)}
                              />
                            )}

                            {/* Clear All Filters */}
                            {(hiddenByFilters || filterSportsbooks.length > 0 || filterMarkets.length > 0 || filterPlayers.length > 0 || filterResultType !== 'all' || filterDeepLinksOnly || filterLineType !== 'all' || searchText || sortMethod !== (userProfile?.preferences?.defaultSortMethod || 'ev')) && (
                              <PressableScale onPress={clearAllFilters} style={styles.clearAllButton} hoverStyle={styles.clearAllButtonHovered}>
                                <Text style={styles.clearAllButtonText}>✕ Clear All Filters</Text>
                              </PressableScale>
                            )}
                          </View>
  );
}
